/**
 * unlockStaleOpLocks.js
 *
 * FT-481: Self-healing one-time IDB migration for stale operation_data locks.
 *
 * PROBLEM:
 *   startOperationV2() creates an operation_data log entry with locked:true.
 *   Prior to FT-481, completeOperationV2() and bulkSkipAndComplete() never set
 *   it back to locked:false. Users who completed voyages before this fix have
 *   stale locked:true entries — including Ferrans' iPhone.
 *
 * FIX:
 *   On startup, scan ALL operations_voyages_* records in IDB. For any voyage with
 *   an operation_data entry where locked===true AND a corresponding operation_complete
 *   entry with the same operationId exists, set locked:false (operation finished —
 *   lock is stale). If no operation_complete exists, leave locked:true (genuinely
 *   in-progress). Write the corrected record back to IDB silently (no sr:storage:set
 *   event — React is not mounted when this runs).
 *
 * GUARD:
 *   localStorage flag sr.migration.unlockStaleOpLocks_v1 = 'complete' prevents
 *   re-runs. Written at the END, only on success or explicit skip (no stale data
 *   found). On error: flag is NOT set so the migration retries on next boot.
 *
 * PATTERN:
 *   Follows the cleanupDuplicatePhaseOps pattern (localStorage flag guard, try/catch,
 *   console log on outcomes) but reads/writes IDB (Dexie) instead of localStorage
 *   because operations_voyages data lives in the IDB 'storage' table.
 *
 * CALLER:
 *   src/index.js — fire-and-forget async call AFTER root.render() so React is
 *   already mounted before the write completes. Using sr:storage:set would trigger
 *   a re-render on the existing mounted components, which is fine at that point.
 *   We skip the event here and let the normal IDB→React hydration surface the
 *   corrected data on next vessel context load.
 */

import { db } from '../../lib/db';
import { extractModule } from '../../lib/db';

const MIGRATION_FLAG = 'sr.migration.unlockStaleOpLocks_v1';

/**
 * Run the stale-lock unlock migration.
 * @returns {Promise<boolean>} - Resolves true if migration ran, false if skipped.
 */
export const unlockStaleOpLocks = async () => {
  // Guard: already done on this device.
  if (localStorage.getItem(MIGRATION_FLAG) === 'complete') {
    return false;
  }

  try {
    console.log('[FT-481] Starting stale operation_data lock migration...');

    // Fetch all IDB records whose module is 'operations_voyages'.
    // Dexie index on 'module' makes this efficient.
    const voyageRecords = await db.storage
      .where('module')
      .equals('operations_voyages')
      .toArray();

    if (voyageRecords.length === 0) {
      console.log('[FT-481] No operations_voyages records in IDB — nothing to migrate.');
      localStorage.setItem(MIGRATION_FLAG, 'complete');
      return true;
    }

    let keysChecked = 0;
    let voyagesPatched = 0;
    let locksCleared = 0;

    for (const record of voyageRecords) {
      keysChecked++;

      // record.value is already the parsed JS value (Dexie stores structured clone).
      const voyages = record.value;
      if (!Array.isArray(voyages)) continue;

      let recordDirty = false;

      const patchedVoyages = voyages.map(voyage => {
        const log = voyage.unifiedLog;
        if (!Array.isArray(log) || log.length === 0) return voyage;

        // Build a set of operationIds that have a matching operation_complete entry.
        const completedOpIds = new Set(
          log
            .filter(e => e.type === 'operation_complete' && e.operationId)
            .map(e => e.operationId)
        );

        if (completedOpIds.size === 0) return voyage;

        // Unlock operation_data entries whose operationId is completed.
        let voyageDirty = false;
        const patchedLog = log.map(e => {
          if (
            e.type === 'operation_data' &&
            e.locked === true &&
            e.operationId &&
            completedOpIds.has(e.operationId)
          ) {
            voyageDirty = true;
            locksCleared++;
            console.log(
              `[FT-481] Clearing stale lock: voyageId=${voyage.id} operationId=${e.operationId}`
            );
            return { ...e, locked: false };
          }
          return e;
        });

        if (!voyageDirty) return voyage;

        recordDirty = true;
        return { ...voyage, unifiedLog: patchedLog };
      });

      if (recordDirty) {
        // Write back to IDB. We do a raw put here (no sr:storage:set event) because
        // this migration runs before or alongside React mount — emitting the event
        // could race with the initial hydration. The corrected data will be read by
        // useStorageState on the next IDB hydration cycle.
        await db.storage.put({
          ...record,
          value: patchedVoyages,
          timestamp: Date.now()
        });
        voyagesPatched++;
      }
    }

    // Mark complete — only on success.
    localStorage.setItem(MIGRATION_FLAG, 'complete');

    if (locksCleared > 0) {
      console.log(
        `[FT-481] Migration complete: ${locksCleared} stale lock(s) cleared across ` +
        `${voyagesPatched} IDB record(s) (${keysChecked} checked).`
      );
    } else {
      console.log(
        `[FT-481] Migration complete: no stale locks found in ${keysChecked} IDB record(s).`
      );
    }

    return true;

  } catch (error) {
    console.error('[FT-481] Stale lock migration failed:', error);
    // Do NOT set the flag — retry on next boot so the issue doesn't persist.
    return false;
  }
};
